/**
 * The reporting browser's FAMILY, as a closed metric label (#7696).
 *
 * The 2026-09-25 relay census of `web_maps_degraded` could attribute its 1,511
 * Maps script-load failures to a browser only by STACK-FRAME FORMAT (V8 vs
 * JSC/Gecko), because no report carried a user agent. This answers the
 * question directly, so the next census is a group-by.
 *
 * The vocabulary is NOT declared here. It is `web_browser_family` in
 * `web/manifests/metrics.yaml`, and the type is the GENERATED
 * `WebMapsDegradedBrowserFamily` union — so this file can return no family the
 * metric does not declare, and the BFF (`server/api/browser-metrics.ts`) drops
 * any value outside the same vocabulary. There is no hand-written string union
 * anywhere in this derivation; the brand and token tables below are
 * `as const` data whose family column is typed by the generated union.
 *
 * Two sources, in order of trust:
 *
 *   1. UA Client Hints (`navigator.userAgentData.brands`) — Chromium only, and
 *      the only place an embedder names itself honestly ("Microsoft Edge",
 *      "Opera", "Samsung Internet", "Android WebView", "HeadlessChrome").
 *   2. The `User-Agent` string — every other browser, and Chromium where
 *      Client Hints are absent (older builds, some WebViews).
 *
 * ORDER MATTERS in both tables: every Chromium embedder ALSO claims "Chrome"
 * (and every browser claims "Safari"), so the specific rows come first and the
 * generic ones last. The first match wins.
 */
import type { WebMapsDegradedBrowserFamily } from '@/shared/lib/metrics/client-metrics_gen';

/** The browser family label (the generated `web_browser_family` vocabulary). */
export type BrowserFamily = WebMapsDegradedBrowserFamily;

/** One UA-CH brand → family row. `brand` matches a `brands[].brand` exactly. */
interface BrandRow {
  readonly brand: string;
  readonly family: BrowserFamily;
}

/**
 * UA-CH brands, specific before generic. "Chromium" alone is NOT a row: every
 * Chromium build lists it, so it says nothing about which one this is.
 */
const BRAND_ROWS: readonly BrandRow[] = [
  { brand: 'HeadlessChrome', family: 'headless_chrome' },
  { brand: 'Microsoft Edge', family: 'edge' },
  { brand: 'Opera', family: 'opera' },
  { brand: 'Samsung Internet', family: 'samsung_internet' },
  { brand: 'Android WebView', family: 'android_webview' },
  { brand: 'Google Chrome', family: 'chrome' },
] as const;

/** One UA-string token → family row. `token` is a case-sensitive substring. */
interface TokenRow {
  readonly token: string;
  readonly family: BrowserFamily;
}

/**
 * UA-string tokens, specific before generic:
 *
 *   * `HeadlessChrome` — before `Chrome/`, which it also contains.
 *   * `Edg/` (desktop/Android Edge), `EdgA/`, `EdgiOS/` before `Chrome/`.
 *   * `OPR/` before `Chrome/`.
 *   * `SamsungBrowser/` before `Chrome/`.
 *   * `; wv)` — the Android WebView marker, before `Chrome/`.
 *   * `Firefox/` and `FxiOS/` — Gecko, and Firefox on iOS.
 *   * `CriOS/` — Chrome on iOS (WebKit underneath, but the user chose Chrome).
 *   * `Chrome/` — any remaining Chromium.
 *   * `Safari/` — LAST: every Chromium UA also carries it.
 */
const TOKEN_ROWS: readonly TokenRow[] = [
  { token: 'HeadlessChrome', family: 'headless_chrome' },
  { token: 'Edg/', family: 'edge' },
  { token: 'EdgA/', family: 'edge' },
  { token: 'EdgiOS/', family: 'edge' },
  { token: 'OPR/', family: 'opera' },
  { token: 'SamsungBrowser/', family: 'samsung_internet' },
  { token: '; wv)', family: 'android_webview' },
  { token: 'Firefox/', family: 'firefox' },
  { token: 'FxiOS/', family: 'firefox' },
  { token: 'CriOS/', family: 'chrome' },
  { token: 'Chrome/', family: 'chrome' },
  { token: 'Safari/', family: 'safari' },
] as const;

/** The two inputs, as the browser exposes them. Brands may be absent. */
export interface BrowserIdentity {
  readonly userAgent: string;
  readonly brands?: readonly { readonly brand: string }[];
}

/** WeChat's in-app browser names itself only by this UA token. */
const WECHAT_TOKEN = 'MicroMessenger';

/**
 * Headless Chromium's UA token. Checked before the brands too: a headless
 * build can report generic Chromium brands while its UA still says
 * `HeadlessChrome`, and the headless population is exactly the one this label
 * exists to separate (the 2026-09-18 sweep).
 */
const HEADLESS_TOKEN = 'HeadlessChrome';

/**
 * Derive the family. Pure: a test passes the identity, the app passes
 * {@link currentBrowserIdentity}.
 *
 * WeChat (`MicroMessenger`, its in-app browser: Chromium/XWEB on Android,
 * WKWebView on iOS) is checked against the UA string BEFORE Client Hints: on
 * Android its brands are generic Chromium ones, and it names itself only in
 * the UA. It is the dominant browser on the zh-CN pages that fail at 2.5× the
 * other locales' rate, so it gets its own row rather than folding into
 * `chrome` / `android_webview`.
 */
export function deriveBrowserFamily(identity: BrowserIdentity): BrowserFamily {
  const ua = identity.userAgent;
  if (ua.includes(WECHAT_TOKEN)) return 'wechat';
  if (ua.includes(HEADLESS_TOKEN)) return 'headless_chrome';
  for (const row of BRAND_ROWS) {
    if (identity.brands?.some((b) => b.brand === row.brand)) return row.family;
  }
  for (const row of TOKEN_ROWS) {
    if (ua.includes(row.token)) return row.family;
  }
  return 'other';
}

/** The minimal UA-CH surface read here (absent from TS's DOM lib). */
interface NavigatorUAData {
  readonly brands?: readonly { readonly brand: string }[];
}

/** This browser's identity, or an empty one outside a browser (SSR). */
export function currentBrowserIdentity(): BrowserIdentity {
  if (typeof navigator === 'undefined') return { userAgent: '' };
  const uaData = (navigator as Navigator & { userAgentData?: NavigatorUAData }).userAgentData;
  return { userAgent: navigator.userAgent, brands: uaData?.brands };
}
